// ─── Module Browser ─────────────────────────────────────────────
// Browse and install pre-built infrastructure modules for a project.
// Grid view with category filtering, detail slide-out, dependency resolution.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, CreditCard, Mail, LayoutDashboard, BarChart3,
  Server, Layout, Database, HardDrive, Webhook, Bell,
  ToggleLeft, Rocket, Search, X, Plus, Check, ChevronRight,
  Package, Loader2, AlertCircle,
} from 'lucide-react';
import api, { type ModuleRegistryEntry, type InstalledModule } from '../api';

// ─── Icon mapping ──────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  Shield: <Shield size={20} />,
  CreditCard: <CreditCard size={20} />,
  Mail: <Mail size={20} />,
  LayoutDashboard: <LayoutDashboard size={20} />,
  BarChart3: <BarChart3 size={20} />,
  Server: <Server size={20} />,
  Layout: <Layout size={20} />,
  Database: <Database size={20} />,
  HardDrive: <HardDrive size={20} />,
  Webhook: <Webhook size={20} />,
  Bell: <Bell size={20} />,
  ToggleLeft: <ToggleLeft size={20} />,
  Rocket: <Rocket size={20} />,
};

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Auth',
  billing: 'Billing',
  communication: 'Communication',
  admin: 'Admin',
  analytics: 'Analytics',
  scaffold: 'Scaffold',
  database: 'Database',
  storage: 'Storage',
  integration: 'Integration',
  devops: 'DevOps',
};

const CATEGORY_COLORS: Record<string, string> = {
  auth: '#818cf8',
  billing: '#22c55e',
  communication: '#00E5FF',
  admin: '#a855f7',
  analytics: '#3b82f6',
  scaffold: '#06b6d4',
  database: '#4ade80',
  storage: '#eab308',
  integration: '#ec4899',
  devops: '#00E5FF',
};

// ─── Types ──────────────────────────────────────────────────────

interface ModuleBrowserProps {
  projectId: string;
  onClose?: () => void;
}

// ─── Component ──────────────────────────────────────────────────

export const ModuleBrowser: React.FC<ModuleBrowserProps> = ({ projectId, onClose }) => {
  const [modules, setModules] = useState<ModuleRegistryEntry[]>([]);
  const [installed, setInstalled] = useState<InstalledModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [selected, setSelected] = useState<ModuleRegistryEntry | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [addResult, setAddResult] = useState<{ slug: string; deps: string[] } | null>(null);

  const load = useCallback(async () => {
    try {
      const [mods, inst] = await Promise.all([
        api.listModules({ category: categoryFilter || undefined }),
        api.getProjectModules(projectId),
      ]);
      setModules(mods);
      setInstalled(inst);
    } catch (err) {
      console.error('[ModuleBrowser] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const isInstalled = (slug: string): boolean =>
    installed.some((i: InstalledModule) => i.module.slug === slug);

  const handleAdd = async (mod: ModuleRegistryEntry) => {
    setAdding(mod.slug);
    setAddResult(null);
    try {
      const result = await api.addProjectModule(projectId, mod.slug);
      setAddResult({ slug: mod.slug, deps: result.dependencies_added || [] });
      await load(); // Refresh installed list
    } catch (err: unknown) {
      console.error('[ModuleBrowser] Add error:', err);
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (moduleId: string) => {
    try {
      await api.removeProjectModule(projectId, moduleId);
      await load();
    } catch (err) {
      console.error('[ModuleBrowser] Remove error:', err);
    }
  };

  const filtered = modules.filter((m: ModuleRegistryEntry) => {
    if (search) {
      const q = search.toLowerCase();
      if (!m.display_name.toLowerCase().includes(q) && !m.description.toLowerCase().includes(q) && !m.slug.includes(q)) return false;
    }
    return true;
  });

  const categories = [...new Set(modules.map((m: ModuleRegistryEntry) => m.category))].sort();

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: 'var(--bg)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--surface)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Package size={18} style={{ color: 'var(--orange)' }} />
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text)', fontWeight: 600 }}>Module Registry</h2>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {installed.length} installed
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
          }}>
            <Search size={13} style={{ color: 'var(--text-muted)' }} />
            <input
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              placeholder="Search modules..."
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--text)', fontSize: 12, width: 160,
                fontFamily: 'var(--font-sans)',
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                <X size={12} />
              </button>
            )}
          </div>
          {onClose && (
            <button onClick={onClose} style={{
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '5px 10px',
              cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12,
            }}>
              Close
            </button>
          )}
        </div>
      </div>

      {/* Category filter */}
      <div style={{
        padding: '8px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', gap: 6, flexWrap: 'wrap',
        background: 'var(--surface)',
      }}>
        <FilterChip label="All" active={!categoryFilter} onClick={() => setCategoryFilter('')} />
        {categories.map((cat: string) => (
          <FilterChip
            key={cat}
            label={CATEGORY_LABELS[cat] || cat}
            active={categoryFilter === cat}
            onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
            color={CATEGORY_COLORS[cat]}
          />
        ))}
      </div>

      {/* Dependency auto-add confirmation */}
      {addResult && addResult.deps.length > 0 && (
        <div style={{
          margin: '8px 20px 0', padding: '8px 12px',
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-xs)', color: 'var(--green)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Check size={13} />
          Added <strong>{addResult.slug}</strong> with dependencies: {addResult.deps.join(', ')}
          <button onClick={() => setAddResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 2 }}>
            <X size={12} />
          </button>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Module grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />
              Loading modules...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No modules found.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12,
            }}>
              {filtered.map((mod: ModuleRegistryEntry) => {
                const inst = isInstalled(mod.slug);
                const isAdding = adding === mod.slug;
                const isComingSoon = mod.status === 'coming_soon';

                return (
                  <div
                    key={mod.id}
                    onClick={() => setSelected(mod)}
                    style={{
                      padding: 16,
                      background: 'var(--surface)',
                      border: `1px solid ${inst ? 'var(--green)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-lg)',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s ease, transform 0.1s ease',
                      opacity: isComingSoon ? 0.6 : 1,
                    }}
                    onMouseEnter={e => { if (!isComingSoon) e.currentTarget.style.borderColor = 'var(--orange)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = inst ? 'var(--green)' : 'var(--border)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 'var(--radius-md)',
                        background: 'var(--surface2)', border: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: CATEGORY_COLORS[mod.category] || 'var(--text-dim)',
                        flexShrink: 0,
                      }}>
                        {ICON_MAP[mod.icon || ''] || <Package size={20} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                            {mod.display_name}
                          </span>
                          {inst && <Check size={13} style={{ color: 'var(--green)' }} />}
                        </div>
                        <div style={{
                          fontSize: 12, color: 'var(--text-muted)',
                          lineHeight: 1.4,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {mod.description}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                          <span style={{
                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                            background: `color-mix(in srgb, ${CATEGORY_COLORS[mod.category] || '#888'} 15%, transparent)`,
                            color: CATEGORY_COLORS[mod.category] || '#888',
                          }}>
                            {CATEGORY_LABELS[mod.category] || mod.category}
                          </span>
                          {isComingSoon && (
                            <span style={{
                              padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                              background: 'rgba(234,179,8,0.1)', color: '#eab308',
                            }}>
                              Coming Soon
                            </span>
                          )}
                          {mod.dependencies && mod.dependencies.length > 0 && (
                            <span style={{ fontSize: 10, color: 'var(--text-ghost)', fontFamily: 'var(--font-mono)' }}>
                              deps: {mod.dependencies.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail slide-out */}
        {selected && (
          <div style={{
            width: 360, borderLeft: '1px solid var(--border)',
            background: 'var(--surface)', overflowY: 'auto',
            flexShrink: 0,
          }}>
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 'var(--radius-lg)',
                  background: 'var(--surface2)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: CATEGORY_COLORS[selected.category] || 'var(--text-dim)',
                }}>
                  {ICON_MAP[selected.icon || ''] || <Package size={24} />}
                </div>
                <button onClick={() => setSelected(null)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', padding: 4,
                }}>
                  <X size={16} />
                </button>
              </div>

              <h3 style={{ margin: '0 0 4px', fontSize: 18, color: 'var(--text)', fontWeight: 600 }}>
                {selected.display_name}
              </h3>
              <div style={{ fontSize: 12, color: 'var(--text-ghost)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
                {selected.name}
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, margin: '0 0 16px' }}>
                {selected.description}
              </p>

              {/* Metadata */}
              <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                <DetailRow label="Category" value={CATEGORY_LABELS[selected.category] || selected.category} />
                <DetailRow label="Tier" value={selected.tier} />
                <DetailRow label="Status" value={selected.status} />
                {selected.source_product && <DetailRow label="Source" value={selected.source_product} />}
              </div>

              {/* Dependencies */}
              {selected.dependencies && selected.dependencies.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Dependencies
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {selected.dependencies.map((dep: string) => (
                      <span key={dep} style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11,
                        background: 'var(--surface2)', border: '1px solid var(--border)',
                        color: isInstalled(dep) ? 'var(--green)' : 'var(--text-dim)',
                        fontFamily: 'var(--font-mono)',
                      }}>
                        {dep} {isInstalled(dep) ? '✓' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action button */}
              {isInstalled(selected.slug) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0' }}>
                  <Check size={16} style={{ color: 'var(--green)' }} />
                  <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Installed</span>
                </div>
              ) : selected.status === 'coming_soon' ? (
                <button disabled style={{
                  width: '100%', padding: '10px 16px',
                  background: 'var(--surface2)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)', color: 'var(--text-muted)',
                  fontSize: 13, cursor: 'not-allowed',
                }}>
                  Coming Soon
                </button>
              ) : (
                <button
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleAdd(selected); }}
                  disabled={adding === selected.slug}
                  style={{
                    width: '100%', padding: '10px 16px',
                    background: adding === selected.slug ? 'var(--surface3)' : 'var(--orange)',
                    border: 'none', borderRadius: 'var(--radius-md)',
                    color: adding === selected.slug ? 'var(--text-dim)' : 'white',
                    fontSize: 13, fontWeight: 600, cursor: adding === selected.slug ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    boxShadow: adding ? 'none' : 'var(--orange-glow)',
                  }}
                >
                  {adding === selected.slug
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Adding...</>
                    : <><Plus size={14} /> Add to Project</>}
                </button>
              )}

              {/* Dependency auto-add note */}
              {!isInstalled(selected.slug) && selected.dependencies && selected.dependencies.length > 0 && selected.status === 'available' && (
                <div style={{
                  marginTop: 8, padding: '6px 10px',
                  fontSize: 11, color: 'var(--text-muted)',
                  background: 'var(--surface2)', borderRadius: 'var(--radius-sm)',
                }}>
                  <AlertCircle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                  Dependencies will be auto-installed: {selected.dependencies.filter((d: string) => !isInstalled(d)).join(', ') || 'all satisfied'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Module Chips (for Overview page) ──────────────────────────

export const InstalledModuleChips: React.FC<{
  projectId: string;
  onBrowse?: () => void;
}> = ({ projectId, onBrowse }) => {
  const [installed, setInstalled] = useState<InstalledModule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getProjectModules(projectId)
      .then(setInstalled)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <span style={{
          fontSize: 11, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600,
        }}>
          Modules ({installed.length})
        </span>
        {onBrowse && (
          <button onClick={onBrowse} style={{
            padding: '3px 8px', fontSize: 'var(--text-xs)',
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', color: 'var(--text-dim)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <Package size={11} /> Browse
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {installed.length === 0 ? (
          <span style={{ fontSize: 12, color: 'var(--text-ghost)' }}>No modules installed</span>
        ) : (
          installed.map((inst: InstalledModule) => (
            <span key={inst.id} style={{
              padding: '3px 8px', borderRadius: 'var(--radius-sm)',
              background: 'var(--surface2)', border: '1px solid var(--border)',
              fontSize: 11, color: 'var(--text-dim)',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <div style={{ color: CATEGORY_COLORS[inst.module.category] || 'var(--text-muted)' }}>
                <Package size={11} />
              </div>
              {inst.module.display_name}
            </span>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Helpers ────────────────────────────────────────────────────

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  color?: string;
}> = ({ label, active, onClick, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '3px 10px', borderRadius: 'var(--radius-pill)',
      background: active ? (color ? `color-mix(in srgb, ${color} 15%, transparent)` : 'var(--orange-dim)') : 'var(--surface2)',
      border: `1px solid ${active ? (color || 'var(--orange)') : 'var(--border)'}`,
      color: active ? (color || 'var(--orange)') : 'var(--text-muted)',
      fontSize: 11, fontWeight: active ? 600 : 400,
      cursor: 'pointer', transition: 'all 0.15s ease',
    }}
  >
    {label}
  </button>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
    <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{value}</span>
  </div>
);
